import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, Check, CreditCard, Database, Fingerprint, Globe, Infinity as InfinityIcon, Smartphone } from "lucide-react";
import { cn } from "../../../utils/function";
import { SourceIconKey } from "./data";

// Shared building blocks for the Unify screens.

const SOURCE_ICON: Record<SourceIconKey, { icon: React.ElementType; cls: string }> = {
  convohub: { icon: Database, cls: "text-indigo-600 dark:text-indigo-400" },
  core_banking: { icon: CreditCard, cls: "text-amber-500" },
  kyc: { icon: Fingerprint, cls: "text-lime-600" },
  website: { icon: Globe, cls: "text-blue-600" },
  mobile: { icon: Smartphone, cls: "text-rose-500" },
  ga: { icon: BarChart3, cls: "text-orange-500" },
  meta: { icon: InfinityIcon, cls: "text-blue-600" },
};

export const SourceIcon: React.FC<{ id: SourceIconKey; size?: number }> = ({ id, size = 20 }) => {
  const { icon: Icon, cls } = SOURCE_ICON[id];
  return <Icon size={size} className={cn("shrink-0", cls)} />;
};

export const card = "bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800";
export const primaryBtn =
  "inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-700 hover:bg-indigo-800 rounded-lg transition-colors disabled:opacity-50";
export const secondaryBtn =
  "inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors";
export const linkCls = "text-sm font-medium text-indigo-700 dark:text-indigo-400 underline underline-offset-2 hover:text-indigo-800";

export const fmt = (n: number) => n.toLocaleString("en-US");

export const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; className?: string; action?: React.ReactNode }> = ({ title, subtitle, children, className, action }) => (
  <section className={className}>
    <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
      <div>
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h2>
        {subtitle && <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

export const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  description?: React.ReactNode;
  valueClass?: string;
  labelClass?: string;
  footer?: React.ReactNode;
}> = ({ icon, label, value, description, valueClass, labelClass, footer }) => (
  <div className={cn(card, "p-4 flex flex-col")}>
    <p className={cn("flex items-start gap-2 text-sm sm:text-[15px] text-gray-900 dark:text-gray-100 leading-snug", labelClass)}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      {label}
    </p>
    <p className={cn("mt-3 text-xl sm:text-2xl text-gray-900 dark:text-gray-100", valueClass)}>{value}</p>
    {description && <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>}
    {footer && <div className="mt-auto pt-2">{footer}</div>}
  </div>
);

export const Pill: React.FC<{ tone?: "green" | "yellow" | "orange" | "gray" | "blue"; dot?: boolean; children: React.ReactNode; className?: string }> = ({ tone = "gray", dot, children, className }) => {
  const tones = {
    green: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
    yellow: "bg-yellow-200 text-yellow-900 dark:bg-yellow-500/15 dark:text-yellow-300",
    orange: "bg-orange-500 text-white",
    gray: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    blue: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  };
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap", tones[tone], className)}>
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />}
      {children}
    </span>
  );
};

export const PageHeader: React.FC<{ crumbs: string[]; back: string; children?: React.ReactNode }> = ({ crumbs, back, children }) => {
  const navigate = useNavigate();
  return (
    <div className={cn(card, "rounded-b-none border-b-0 px-4 sm:px-6 pt-5 pb-5 space-y-5")}>
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={() => navigate(back)}
          aria-label="Back"
          className="w-9 h-9 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg sm:text-2xl text-gray-400 truncate">
          {crumbs.map((c, i) => (
            <React.Fragment key={c}>
              {i > 0 && <span className="text-gray-700 dark:text-gray-300 mx-1.5">/</span>}
              <span className={i === crumbs.length - 1 ? "text-gray-900 dark:text-gray-100" : ""}>{c}</span>
            </React.Fragment>
          ))}
        </h1>
      </div>
      {children}
    </div>
  );
};

export const Stepper: React.FC<{ steps: string[]; step: number; onStep?: (i: number) => void }> = ({ steps, step, onStep }) => (
  <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1">
    {steps.map((name, i) => (
      <React.Fragment key={name}>
        <button type="button" onClick={() => onStep?.(i)} className="flex items-center gap-2 shrink-0">
          <span
            className={cn(
              "w-7 h-7 rounded-md flex items-center justify-center text-sm font-medium border",
              i < step
                ? "rounded-full bg-emerald-400 border-emerald-500 text-white"
                : i === step
                  ? "bg-emerald-100 border-emerald-500 text-emerald-800"
                  : "bg-indigo-50 border-indigo-100 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
            )}
          >
            {i < step ? <Check size={15} /> : i + 1}
          </span>
          <span className={cn("text-sm sm:text-base whitespace-nowrap", i <= step ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400")}>{name}</span>
        </button>
        {i < steps.length - 1 && <span className={cn("flex-1 min-w-[24px] h-0.5 rounded", i < step ? "bg-gray-800 dark:bg-gray-300" : "bg-gray-800/80 dark:bg-gray-600")} />}
      </React.Fragment>
    ))}
  </div>
);

export const Tabs: React.FC<{ tabs: string[]; active: string; onChange: (t: string) => void }> = ({ tabs, active, onChange }) => (
  <div className="flex gap-1 sm:gap-4 border-b border-gray-200 dark:border-gray-800 overflow-x-auto">
    {tabs.map((t) => (
      <button
        key={t}
        type="button"
        onClick={() => onChange(t)}
        className={cn(
          "px-3 sm:px-4 pb-2.5 pt-1 text-sm sm:text-[15px] whitespace-nowrap border-b-2 -mb-px transition-colors",
          active === t ? "border-indigo-600 text-indigo-700 dark:text-indigo-400" : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800"
        )}
      >
        {t}
      </button>
    ))}
  </div>
);

export const Avatar: React.FC<{ name: string; size?: string }> = ({ name, size = "w-10 h-10" }) => (
  <span className={cn(size, "rounded-full bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-200 flex items-center justify-center text-sm font-semibold shrink-0")}>
    {name
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("")}
  </span>
);
